import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowDownTrayIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = { teal:'#00B4D8', success:'#10B981', warning:'#F59E0B', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', navy:'#0D1B2A' };

export default function TutorEarnings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/tutor/dashboard').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Layout role="tutor"><div style={{ textAlign:'center', padding:80, color:C.grey }}>Loading earnings...</div></Layout>;

  const earnings = data?.earnings || [];
  const totals = data?.totals || { gross:0, fees:0, net:0 };
  const feeEnabled = data?.fee_info?.enabled !== false;
  const feePercent = data?.fee_info?.percent || 10;
  const feeLabel = data?.fee_info?.label || 'Platform Fee';

  // Build monthly chart
  const monthlyMap = {};
  earnings.forEach(e => {
    const d = new Date(e.created_at);
    const key = d.toLocaleString('en-GB', { month:'short', year:'numeric' });
    const shortKey = d.toLocaleString('en-GB', { month:'short' });
    if (!monthlyMap[key]) monthlyMap[key] = { month: shortKey, gross:0, net:0 };
    monthlyMap[key].gross += e.gross_rate_usd || 0;
    monthlyMap[key].net += e.net_usd || 0;
  });
  const chartData = Object.values(monthlyMap).slice(-6).map(d => ({
    month: d.month,
    gross: parseFloat(d.gross.toFixed(2)),
    net: parseFloat(d.net.toFixed(2)),
  }));

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
  const formatType = (t) => (t||'Lesson').split('_').map(w=>w[0].toUpperCase()+w.slice(1)).join(' ');

  return (
    <Layout role="tutor">
      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:20, marginBottom:24 }}>
        {[
          { label:'Gross Earned', value:`$${totals.gross.toFixed(2)}`, color:C.teal, sub:`${earnings.length} lessons` },
          { label: feeLabel, value:`-$${totals.fees.toFixed(2)}`, color:C.warning, sub: feeEnabled ? `${feePercent}% fee` : 'No fees' },
          { label:'Net Payout', value:`$${totals.net.toFixed(2)}`, color:C.success, sub:'Your take-home' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="card" style={{ padding:24 }}>
            <div style={{ fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:8 }}>{label}</div>
            <div style={{ fontSize:28, fontWeight:800, color }}>{value}</div>
            <div style={{ fontSize:12, color:C.grey, marginTop:4 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <div className="card" style={{ padding:24, marginBottom:24 }}>
          <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Monthly Earnings History</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="month" tick={{ fontSize:12 }} />
              <YAxis tick={{ fontSize:12 }} tickFormatter={v=>`$${v}`} />
              <Tooltip formatter={(v, name) => [`$${v}`, name === 'net' ? 'Net' : 'Gross']} />
              {feeEnabled && <Bar dataKey="gross" fill={`${C.teal}60`} radius={[3,3,0,0]} name="Gross" />}
              <Bar dataKey="net" fill={C.success} radius={[3,3,0,0]} name="Net" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="card" style={{ padding:48, marginBottom:24, textAlign:'center' }}>
          <CurrencyDollarIcon style={{ width:48, height:48, color:C.border, margin:'0 auto 16px', display:'block' }} />
          <h3 style={{ margin:'0 0 8px', color:C.dark }}>No earnings yet</h3>
          <p style={{ color:C.grey, fontSize:14, margin:0 }}>Your earnings will appear here after you complete lessons with students.</p>
        </div>
      )}

      {/* Lesson breakdown table */}
      {earnings.length > 0 && (
        <div className="card" style={{ overflow:'hidden' }}>
          <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Lesson Breakdown</h3>
            <button style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', background:'#F3F4F6', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
              <ArrowDownTrayIcon style={{ width:14, height:14 }} /> Download Statement
            </button>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#F9FAFB' }}>
                  {['Date','Lesson Type','Duration','Gross',feeEnabled ? `${feeLabel} (${feePercent}%)` : null,'Net'].filter(Boolean).map(h => (
                    <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase', letterSpacing:'0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {earnings.map((e, i) => (
                  <tr key={e.id || i} style={{ borderTop:`1px solid ${C.border}` }}>
                    <td style={{ padding:'12px 16px', color:C.grey, fontSize:13 }}>{formatDate(e.created_at)}</td>
                    <td style={{ padding:'12px 16px', fontWeight:600 }}>{formatType(e.lesson_type)}</td>
                    <td style={{ padding:'12px 16px', color:C.grey }}>60 min</td>
                    <td style={{ padding:'12px 16px' }}>${(e.gross_rate_usd||0).toFixed(2)}</td>
                    {feeEnabled && <td style={{ padding:'12px 16px', color:C.warning }}>-${(e.platform_fee_usd||0).toFixed(2)}</td>}
                    <td style={{ padding:'12px 16px', fontWeight:700, color:C.success }}>${(e.net_usd||0).toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop:`2px solid ${C.border}`, background:'#F9FAFB' }}>
                  <td colSpan={3} style={{ padding:'12px 16px', fontWeight:700 }}>Totals</td>
                  <td style={{ padding:'12px 16px', fontWeight:700 }}>${totals.gross.toFixed(2)}</td>
                  {feeEnabled && <td style={{ padding:'12px 16px', fontWeight:700, color:C.warning }}>-${totals.fees.toFixed(2)}</td>}
                  <td style={{ padding:'12px 16px', fontWeight:800, color:C.success, fontSize:15 }}>${totals.net.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}
